<template>
  <div
    ref="selectWrapper"
    class="control is-expanded modern-selector-wrapper"
    :class="{'disabled': disabled}"
  >
        <span class="select input icon is-clickable select-out"
              ref="mainSpanSelector"
              :class="{'is-empty': getSelectedItemName === placeholder,
               'is-danger': isDanger, 'disabled': disabled}"
              @click.stop="toggleListItems($event)"
              @keydown.enter.stop="toggleListItems"
              tabindex="0"
        >
            <div class="select-ext"
            :class="{'placeholder':getSelectedItemName === placeholder}">
                {{ getSelectedItemName }}
            </div>
        </span>
    <div
      v-show="showListItems"
      ref="listItems"
      id="listItems"
      class="items"
     :class="{'list-items-has-search-btn': hasSearchButton}"
      @click.stop
    >
        <template v-if="autocomplete">
          <template v-if="hasSearchButton">
            <b-field class="box-filter-field-search input-wrapper"
                     :class="{'list-items-has-search-btn': hasSearchButton}">
              <b-input expanded customClass="boxfilter-input"
                       :value="searchText"
                       rootClasses="boxfilter-input"
                       class="box-filter-field-search-input boxfilter-input search-input"
                       type="search"
                       ref="boxFilterInput"
                       @input="handleInput"
                       @keydown.native.tab.prevent.stop="tabPressed"
                       @keydown.enter.native="doSearch"
                       @keydown.native.esc.prevent.stop="escPressed"
                       @keydown.native.down.prevent.stop="focusOnSelect"
                       inheritAttrs=true>
              </b-input>
              <p class="control">
                <b-button
                  :disabled="searchText.length < 3"
                  icon-right="magnify"
                  class="is-success gobtn"
                  @click="doSearch"></b-button>
              </p>
            </b-field>
            <p v-if="searchHint" class="search-btn-hint-text">{{ searchHint }}</p>
          </template>
          <template v-else>
            <b-field class="input-wrapper">
              <b-input
                autocomplete="off"
                ref="boxFilterInput"
                :value="searchText"
                :expanded="true"
                :placeholder="searchPlaceholder"
                @input="handleInput"
                @keydown.native.tab.prevent.stop="tabPressed"
                @keydown.native.esc.prevent.stop="escPressed"
                @keydown.native.down.prevent.stop="focusOnSelect"
                class="input-ext"
                tabindex="0"
              >
              </b-input>
            </b-field>
          </template>
        </template>
        <b-select-box
          v-model="selectedItem"
          :options="filteredOptions"
          :has-search-button="hasSearchButton"
          :options-item-name="optionsItemName"
          :visibleItems=8
          :show-not-found-text="showNotFoundText"
          ref="selectBox"
          @keydown="onKeyDown"
          @click="onClick"
          @enter="onClick"
        >
          <template #optionContent="slotProps">
            <slot name="optionContent" v-bind="slotProps"></slot>
          </template>
        </b-select-box>
    </div>
  </div>
</template>

<script>
import BSelectBox from './BSelectBox.vue';

export default {
  components: {
    BSelectBox,
  },
  name: 'BSelectExt',
  props: {
    selectedItem: {
      default: null,
    },
    placeholder: {
      type: String,
      default: null,
    },
    searchPlaceholder: {
      type: String,
      default: null,
    },
    isDanger: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    value: {
      type: String,
      default: null,
    },
    options: {
      type: Array,
      default: () => [],
    },
    optionsItemName: {
      type: String,
      default: 'name',
    },
    autocomplete: {
      type: Boolean,
      default: false,
    },
    hasSearchButton: {
      type: Boolean,
      default: false,
    },
    searchHint: {
      type: String,
      default: 'Search by company name or customer ID.',
    },
    filterCallback: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      selectNativeSize: 5,
      specialIndex: -1, // if search didn't get a result/too much result
      showListItems: false,
      searchText: '',
    };
  },
  computed: {
    getSelectedItemName() {
      if (this.selectedItem && this.selectedItem.id) {
        return this.selectedItem[this.optionsItemName] ?? '';
      }
      return this.selectedItem ?? this.placeholder;
    },
    optionsHasObjects() {
      if (this.options.length > 0) {
        return this.options[0].id !== undefined;
      }
      return false;
    },
    computedNativeSize() {
      const filteredOptionsCount = this.filteredOptions.length;
      return filteredOptionsCount < 5 ? filteredOptionsCount : 5;
    },
    filteredOptions() {
      // if options is an array of strings
      if (this.options.length > 0 && !this.options[0].id) {
        return this.options.filter((option) => option.toLowerCase().includes(this.searchText));
      }
      if (this.options.length === 1 && this.options[0].id === this.specialIndex) {
        return this.options;
      }
      if (!this.searchText || this.hasSearchButton) {
        return this.options;
      }
      const searchTLC = this.searchText.toLowerCase();

      if (this.filterCallback) {
        return this.options.filter((option) => this.filterCallback(searchTLC, option));
      }

      return this.options.filter((option) => {
        if (
          (option[this.optionsItemName].toLowerCase().includes(searchTLC)
          || ('id' in option ? option.id === -1 : false))
          || ('price' in option ? option.price.toFixed(2).toLowerCase().includes(searchTLC) : false)
          || ('id' in option ? option.id.toString().includes(searchTLC) : false)) {
          return true;
        }
        return false;
      });
    },
    showNotFoundText() {
      return this.searchText !== '' && this.filteredOptions.length === 0;
    },
  },
  methods: {
    tabPressed() {
      if (this.filteredOptions.length > 1
        || (this.filteredOptions.length === 1 && this.filteredOptions[0].id > 0)) {
        this.$refs.selectBox.navigateTo(this.filteredOptions[0]);
      } else {
        this.escPressed();
      }
    },
    escPressed() {
      this.showListItems = false;
      this.$emit('escape', this);
    },
    doSearch() {
      this.$emit('search', this.searchText);
    },
    toggleListItems(val) {
      const spanSelector = val.target.parentElement;
      if (spanSelector && !spanSelector.classList.contains('disabled')) {
        this.showListItems = !this.showListItems;
        if (this.showListItems === true) {
          this.$nextTick(() => {
            if (!this.hasSearchButton) {
              if (this.filteredOptions.length) {
                if (this.selectedItem) {
                  this.$refs.selectBox.navigateTo(this.selectedItem);
                } else {
                  this.$refs.selectBox.scrollToTop();
                }
              }
            } else {
              this.$refs.selectBox.scrollToTop();
            }
            this.$refs.boxFilterInput.focus();
          });
          this.$emit('showListItems', this.$refs.listItems);
        }
      }
      this.$emit('hideListItems', this);
    },
    handleClickOutside(event) {
      const { selectWrapper, listItems } = this.$refs;

      if (!selectWrapper || !listItems) {
        return;
      }

      if (!selectWrapper.contains(event.target)) {
        this.showListItems = false;
      }

      this.$refs.selectBox.resetFocus();
    },
    handleInput(newSearchText) {
      this.$refs.selectBox.resetFocus();
      this.searchText = newSearchText;
    },
    focusOnSelect() {
      if (this.filteredOptions.length > 1
        || (this.filteredOptions.length === 1 && this.filteredOptions[0].id > 0)) {
        this.$refs.selectBox.navigateTo(this.filteredOptions[0]);
      }
    },
    onKeyDown(event) {
      if (event.code === 'Escape' || event.code === 'Tab') {
        this.showListItems = false;
        this.$refs.selectBox.resetFocus();
        this.$emit('escape', this);
      } else {
        this.$emit('keydown', event);
      }
      if (event.key.length === 1) {
        this.searchText += event.key;
        this.$refs.selectBox.resetFocus();
      }
      switch (event.code) {
        case 'Backspace':
          this.searchText = this.searchText.slice(0, this.specialIndex);
          break;
        default:
          break;
      }
    },
    onClick(selectedOption) {
      this.searchText = '';
      this.$refs.selectBox.resetFocus();
      this.$emit('input', selectedOption);
      this.showListItems = !this.showListItems;
      if (this.showListItems) {
        this.$emit('showListItems', this.$refs.listItems);
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
    document.addEventListener('mousedown', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
    document.removeEventListener('mousedown', this.handleClickOutside);
  },
};
</script>

<style lang="stylus" scoped>
button.button.is-success.gobtn
  border-radius unset

.search-btn-hint-text
  color #363636
  opacity 0.8
  font-size 14px
  font-weight normal
.select.input
  display flex
.disabled
  background-color whitesmoke
  border-color whitesmoke
.select.input.disabled:hover
  border-color whitesmoke
  cursor not-allowed
.select.input.disabled:after
  border none
.select.input.disabled:focus
  outline none
  box-shadow none
  border-color whitesmoke
.is-empty
  color rgba(122, 122, 122, 0.7);
#listItems > .field
  margin-bottom: 0
  margin-top: 0
#listItems > .field.input-wrapper
  margin-right 0
.items
  width 100%
  position absolute
  cursor default
  z-index 12
#listItems.items.list-items-has-search-btn
  border-left 1px solid #d9d9d9
  border-right 1px solid #d9d9d9
  border-bottom 1px solid #d9d9d9
  background-color white
.box-filter-field-search.list-items-has-search-btn
  padding-top 5px
  padding-left 5px
  padding-right 5px
.search-btn-hint-text
  padding-left 5px
  padding-right 5px

.input-ext
  border-width 0 1px
  border-color #cccccc
  border-style solid
  background-color white
  padding 5px
  .input.is-danger
    border-color #b5b5b5

.select-ext
  width 100%
  overflow hidden
  white-space nowrap
  text-overflow ellipsis
  padding-right 30px
div.select-ext.placeholder
  color #8a9499
.select-out
  box-shadow: none
.modern-selector-wrapper span.select
  font-size 19px
  font-weight 400
  height 48px
.modern-selector-wrapper span.select:hover::after,
.modern-selector-wrapper span.select::after
  border-color #525f66

</style>
